import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../App';
import { toast } from 'react-toastify';

function MyRegistrations({ session, onUpdate }) {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRoomReg, setSelectedRoomReg] = useState(null);

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/registrations/me`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRegistrations(data);
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error('Error fetching registrations:', error);
      toast.error('Failed to load registrations');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (registration) => {
    if (registration.match?.completed) {
      return <span className="badge badge-success">Completed</span>;
    } else if (new Date(registration.match?.start_ts) < new Date()) {
      return <span className="badge badge-warning">In Progress</span>;
    } else {
      return <span className="badge badge-info">Upcoming</span>;
    }
  };

  const getPaymentBadge = (paid) => {
    return paid ? (
      <span className="badge badge-success">✓ Paid</span>
    ) : (
      <span className="badge badge-danger">✗ Pending</span>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
        <p style={{ marginTop: '20px', color: 'var(--text-secondary)' }}>Loading registrations...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{
          fontSize: '2rem',
          marginBottom: '10px',
          fontFamily: 'Orbitron, sans-serif',
          color: 'var(--primary-color)'
        }}>
          📋 My Registrations
        </h2>
        <p style={{ color: 'var(--text-secondary)' }}>
          Track all your tournament registrations
        </p>
      </div>

      {registrations.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: 'var(--secondary-bg)',
          borderRadius: '12px',
          border: '1px solid var(--border-color)'
        }}>
          <p style={{ fontSize: '4rem', marginBottom: '20px' }}>🎯</p>
          <h3 style={{ fontSize: '1.5rem', marginBottom: '10px' }}>No registrations yet</h3>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
            Register for a match to start your gaming journey!
          </p>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Match</th>
                <th>Mode</th>
                <th>Map</th>
                <th>Date & Time</th>
                <th>Entry Fee</th>
                <th>Slot</th>
                <th>Payment</th>
                <th>Status</th>
                <th>Room</th>
              </tr>
            </thead>
            <tbody>
              {registrations.map((reg) => (
                <tr key={reg.id}>
                  <td>
                    <div style={{ fontWeight: '600' }}>{reg.match?.title}</div>
                  </td>
                  <td>
                    <span style={{ textTransform: 'uppercase' }}>
                      {reg.match?.mode}
                    </span>
                  </td>
                  <td>{reg.match?.map}</td>
                  <td>
                    <div>{new Date(reg.match?.start_ts).toLocaleDateString()}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      {new Date(reg.match?.start_ts).toLocaleTimeString()}
                    </div>
                  </td>
                  <td>₹{reg.match?.entry_fee}</td>
                  <td>
                    <span className="badge badge-ghost">#{reg.assigned_slot || 'NA'}</span>
                  </td>
                  <td>{getPaymentBadge(reg.paid)}</td>
                  <td>{getStatusBadge(reg)}</td>
                  <td>
                    {(reg.match?.room_id) ? (
                      <button
                        className="btn btn-sm btn-info"
                        onClick={() => setSelectedRoomReg(reg)}
                      >
                        🔑 Room
                      </button>
                    ) : (
                      <span className="text-muted" style={{ fontSize: '0.8rem', opacity: 0.5 }}>-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Room Info Modal */}
      {selectedRoomReg && (
        <div className="modal-overlay" onClick={() => setSelectedRoomReg(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '350px', textAlign: 'center' }}>
            <h3 style={{ marginBottom: '20px' }}>🔐 Room Details</h3>

            <div style={{ marginBottom: '15px', background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: '8px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '5px' }}>ROOM ID</label>
              <div style={{ fontSize: '1.5rem', fontFamily: 'monospace', fontWeight: 'bold', color: 'var(--primary-color)' }}>
                {selectedRoomReg.match?.room_id}
              </div>
            </div>

            <div style={{ marginBottom: '20px', background: 'rgba(255,255,255,0.05)', padding: '15px', borderRadius: '8px' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '5px' }}>PASSWORD</label>
              <div style={{ fontSize: '1.5rem', fontFamily: 'monospace', fontWeight: 'bold' }}>
                {selectedRoomReg.match?.room_password || 'No Password'}
              </div>
            </div>

            <button className="btn btn-secondary" onClick={() => setSelectedRoomReg(null)} style={{ width: '100%' }}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Registration Cards for Mobile */}
      <div style={{ display: 'none' }} className="mobile-cards">
        {registrations.map((reg) => (
          <div key={reg.id} className="card" style={{ marginBottom: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
              <h4 style={{ fontSize: '1.2rem', fontWeight: '600' }}>{reg.match?.title}</h4>
              {getStatusBadge(reg)}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', fontSize: '0.9rem' }}>
              <div>
                <span style={{ color: 'var(--text-secondary)' }}>Mode:</span>
                <div style={{ textTransform: 'uppercase', fontWeight: '600' }}>{reg.match?.mode}</div>
              </div>
              <div>
                <span style={{ color: 'var(--text-secondary)' }}>Map:</span>
                <div style={{ fontWeight: '600' }}>{reg.match?.map}</div>
              </div>
              <div>
                <span style={{ color: 'var(--text-secondary)' }}>Entry Fee:</span>
                <div style={{ fontWeight: '600' }}>₹{reg.match?.entry_fee}</div>
              </div>
              <div>
                <span style={{ color: 'var(--text-secondary)' }}>Payment:</span>
                <div>{getPaymentBadge(reg.paid)}</div>
              </div>
              <div>
                <span style={{ color: 'var(--text-secondary)' }}>Slot:</span>
                <div><span className="badge badge-ghost">#{reg.assigned_slot || 'NA'}</span></div>
              </div>
              <div>
                <span style={{ color: 'var(--text-secondary)' }}>Room:</span>
                <div style={{ marginTop: '5px' }}>
                  {(reg.match?.room_id) ? (
                    <button
                      className="btn btn-sm btn-info"
                      onClick={() => setSelectedRoomReg(reg)}
                      style={{ padding: '5px 10px', fontSize: '0.8rem' }}
                    >
                      🔑 View Room
                    </button>
                  ) : (
                    <span className="text-muted" style={{ fontSize: '0.8rem', opacity: 0.5 }}>-</span>
                  )}
                </div>
              </div>
            </div>

            <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid var(--border-color)' }}>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '5px' }}>
                📅 {new Date(reg.match?.start_ts).toLocaleDateString()}
              </div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                ⏰ {new Date(reg.match?.start_ts).toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}
      </div>

      <style jsx>{`
        @media (max-width: 768px) {
          .table-container {
            display: none !important;
          }
          .mobile-cards {
            display: block !important;
          }
        }
      `}</style>
    </div>
  );
}

export default MyRegistrations;
